<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted, inject } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { useGuidedElementPicker } from "@/composables/useGuidedElementPicker";
import ElementBrowser from "@/components/atoms/swade/ElementBrowser.vue";
import GuidedScopeFilters from "@/components/atoms/element-filters/GuidedScopeFilters.vue";
import FilterControl from "@/components/atoms/element-filters/FilterControl.vue";
import SkillAttributeFilter from "@/components/atoms/element-filters/SkillAttributeFilter.vue";
import { buildSecondaryChips, clearSecondaryChip } from "@/composables/useElementPredicates";
import Skills from "@/components/swade/Skills.vue";
import type { SwadeCharacterInterface, CharacterSheetInterface } from "@/types/characters";
import type { SettingElementInterface, SwadeSkillInterface } from "@/types/items";
import type { ColumnConfig, FilterChip } from "@/types/browser";
import type { IntlService } from "@progress/kendo-vue-intl";

const props = defineProps<{ stepIndex: number; }>();
const character = defineModel<SwadeCharacterInterface | null>('character');
const kendo: IntlService = inject('kendo') as IntlService;

const ux = useUxData();
const appData = useAppDataStore();
const characterData = useCharacterDataStore();
const setupStore = useGuidedSetupStore();

const {
  browserRef, open, onSelect,
  dataItems, selectedElement, loading, metadata, links,
  onSearch, onNextPage, onPreviousPage, filters,
  showWorldFilter, filtersActive, clearFilters,
} = useGuidedElementPicker('skills', {
  alias: 'skills',
  character: () => character.value,
});

const totalPoints = computed(() => characterData.build?.traits?.total ?? 12);
const allocatedPoints = computed(() => characterData.build?.traits?.allocated ?? 0);
const remainingPoints = computed(() => totalPoints.value - allocatedPoints.value);
const characterSkills = computed(() => character.value?.skills ?? []);

const hasLanguages = computed(() =>
  !!characterData.buildOptions?.languages && !!character.value && !characterData.hasActiveAdvances(character.value));
const languagesAllocated = computed(() => characterData.build?.languages?.allocated ?? 0);
const languagesTotal = computed(() => characterData.build?.languages?.total ?? 0);

const isValid = computed(() => remainingPoints.value === 0);
watch(isValid, (valid) => { setupStore.validateStep(props.stepIndex, valid); }, { immediate: true });

// Keyed off `uuid` alone, so these take the base element type the browser hands back (its
// isAdded/canAdd props are declared on it): a skill that is not on this character simply
// matches no rank. The narrowing to a skill lives in onAdd, the one place that needs it.
const isSkillAdded = (skill: SettingElementInterface): boolean =>
  characterSkills.value.some((sr) => sr.skill?.uuid === skill.uuid);
const isAdded = (skill: SettingElementInterface) => isSkillAdded(skill);
const canAdd = (skill: SettingElementInterface) => !isSkillAdded(skill);

const columns = computed<ColumnConfig[]>(() => [
  { field: 'name', header: ux.labels?.swade?.data?.skills?.name ?? 'Name',
    columnClass: ['name-column'] },
  { field: 'source.name', header: ux.labels?.swade?.data?.skills?.source ?? 'Source', formatter: (el: any) => el.source?.name ?? '' },
  { field: 'linkedAttribute', header: ux.labels?.swade?.data?.skills?.linkedAttribute ?? 'Attribute', formatter: (el: any) => ux.labels?.swade?.attributes?.[el.linkedAttribute ?? ''] ?? '' },
]);

const linkedAttributes = computed<string[]>({
  get: () => (filters.value as any).linkedAttributes ?? [],
  set: (value) => { (filters.value as any).linkedAttributes = value; },
});

// The attribute chips come from the shared per-type mapping the data lists and the world
// browser read, so the wording matches everywhere.
const attributeChips = computed<FilterChip[]>(() => buildSecondaryChips('skills', filters.value as any));
const removeAttribute = (chip: FilterChip) => clearSecondaryChip('skills', filters.value as any, chip);

const onSkillsUpdated = () => {
  if (character.value) {
    characterData.updateProtoSkills(character.value);
    characterData.updateAllocatedSkillPoints(character.value);
    characterData.updateAllocatedLanguages(character.value);
  }
};
const onAdd = (skill: SettingElementInterface) => {
  if (!character.value) return;
  // ElementBrowser's `add` emit is declared on the shared base type, but every row this step
  // can add came from the skills collection (useGuidedElementPicker('skills', …) above).
  characterData.addSkillToCharacter(character.value, skill as SwadeSkillInterface);
  onSkillsUpdated();
};

onMounted(() => {
  // Skills.vue renders editable die-code controls only while appData.props.edit
  // is true; enable it here so skill ranks are adjustable in the guided step.
  appData.props.edit = true;
  setupStore.validateStep(props.stepIndex, isValid.value);
});
onUnmounted(() => { appData.props.edit = false; });
</script>

<template>
  <div class="character-skills-step">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.skillsLabel"></label>
      <div class="d-flex flex-fill gap-2 mb-2">
        <span class="icon mb-auto"><span class="fa-light fa-info-circle"></span></span>
        <p class="form-text my-auto" v-html="ux.labels?.guidedSetup?.character?.skillsHelp"></p>
      </div>

      <div class="points-tracker alert mb-3" :class="remainingPoints === 0 ? 'alert-success' : remainingPoints < 0 ? 'alert-danger' : 'alert-info'">
        <div class="row">
          <div class="col-md">
            <div class="d-flex justify-content-between align-items-center">
              <span>
                <span class="fa-light fa-book me-2"></span>
                <span v-html="ux.labels?.guidedSetup?.character?.skillPoints"></span>
              </span>
              <span class="badge fs-6" :class="remainingPoints === 0 ? 'bg-success' : remainingPoints < 0 ? 'bg-danger' : 'bg-primary'">{{ allocatedPoints }} / {{ totalPoints }}</span>
            </div>
            <small v-if="remainingPoints > 0" class="d-block mt-1" v-html="kendo.format(ux.labels?.guidedSetup?.character?.pointsRemainingFormatString, remainingPoints)"></small>
            <small v-else-if="remainingPoints < 0" class="d-block mt-1 text-danger" v-html="kendo.format(ux.labels?.guidedSetup?.character?.pointsOverFormatString, Math.abs(remainingPoints))"></small>
          </div>
          <div v-if="hasLanguages" class="col-md">
            <div class="d-flex justify-content-between align-items-center">
              <span>
                <span class="fa-light fa-language me-2"></span>
                <span v-html="ux.labels?.swade?.tools?.buildStatusIndicator?.languages"></span>
              </span>
              <span class="badge fs-6" :class="characterData.build?.languages?.fullyAllocated ? 'bg-success' : characterData.build?.languages?.overAllocated ? 'bg-danger' : 'bg-primary'">{{ languagesAllocated }} / {{ languagesTotal }}</span>
            </div>
          </div>
        </div>
      </div>

      <button type="button" class="btn btn-primary w-100 d-lg-none mb-3 add-trigger" @click="open()">
        <span class="fa-light fa-plus"></span>
        {{ ux.labels?.guidedSetup?.character?.addSkills ?? 'Add Skills' }}
      </button>

      <div class="d-flex flex-lg-row flex-column flex-fill gap-3">
        <ElementBrowser
          ref="browserRef"
          :inline="true"
          :title="ux.labels?.guidedSetup?.character?.addSkills ?? 'Add Skills'"
          :data-items="dataItems"
          :columns="columns"
          :selected-element="selectedElement"
          :is-added="isAdded"
          :can-add="canAdd"
          :loading="loading"
          :metadata="metadata"
          :links="links"
          :filters-active="filtersActive"
          @add="onAdd"
          @select="onSelect"
          @search="onSearch"
          @next="onNextPage"
          @previous="onPreviousPage"
          @clear="clearFilters"
        >
          <template #filters>
            <GuidedScopeFilters :show-world="showWorldFilter" />
            <FilterControl
              :label="ux.labels?.swade?.data?.skills?.linkedAttribute ?? 'Attribute'"
              :chips="attributeChips"
              :multiple="false"
              @remove="removeAttribute"
            >
              <SkillAttributeFilter
                v-model:linked-attributes="linkedAttributes"
                :label="ux.labels?.swade?.data?.filters?.labels?.linkedAttribute ?? 'Attribute'"
                :placeholder="ux.labels?.swade?.data?.filters?.attributes ?? 'Attribute'"
              />
            </FilterControl>
          </template>
        </ElementBrowser>

        <Skills v-if="characterSkills.length > 0"
                :character="(character as CharacterSheetInterface)"
                @update:character="(v) => character = (v as SwadeCharacterInterface | null)"
                :show-finder="false" @updated="onSkillsUpdated" />
      </div>
    </div>
  </div>
</template>
